import {
  BufferGeometry,
  ConeGeometry,
  Euler,
  LatheGeometry,
  Matrix4,
  Quaternion,
  SphereGeometry,
  Vector2,
  Vector3,
} from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import type { ProporcoesVitima } from '../../dominio/didatico/perfisVitima'

/**
 * Formas do manequim humano.
 *
 * O manequim era feito de cápsulas de raio constante e de caixas, e por isso lia
 * como "linguiça": um membro humano afina da coxa ao joelho, tem panturrilha,
 * punho fino. Aqui cada segmento é uma superfície de revolução com raio variável
 * ao longo do comprimento, e o tronco é uma seção elíptica que alarga nos ombros
 * e estreita na cintura.
 *
 * A geometria é só aparência: o pivô fica em y = 0 e o
 * comprimento é o do osso, então ângulos, âncoras e trajetórias não mudam.
 * O raio da ponta de um segmento coincide com o do começo do seguinte, e a
 * bola do pivô cobre a emenda: a articulação dobra sem abrir fresta nem degrau.
 */

export type FormaDeMembro = 'braco' | 'antebraco' | 'coxa' | 'perna'

/** Raio relativo ao `raio` informado, em função da posição ao longo do corpo (0 = pivô, 1 = ponta). */
type PerfilRelativo = ReadonlyArray<readonly [t: number, raio: number]>

export const PERFIS_DE_MEMBRO: Readonly<Record<FormaDeMembro, PerfilRelativo>> = {
  // fixados para que o fim de um segmento case com o começo do seguinte (ver teste)
  // o topo do braço é o ombro: a calota maior faz o papel do deltoide
  braco: [[0, 1.04], [0.3, 1], [0.7, 0.9], [1, 0.8]],
  antebraco: [[0, 0.92], [0.25, 1], [0.65, 0.78], [1, 0.62]],
  coxa: [[0, 0.92], [0.25, 1], [0.55, 0.95], [1, 0.74]],
  perna: [[0, 0.93], [0.28, 1], [0.62, 0.8], [1, 0.58]],
}

/** Raio, relativo ao `raio` informado, na posição `t` do perfil (interpolação linear). */
export function raioNoPerfil(perfil: PerfilRelativo, t: number): number {
  const ultimo = perfil.length - 1
  if (t <= perfil[0]![0]) return perfil[0]![1]
  if (t >= perfil[ultimo]![0]) return perfil[ultimo]![1]
  for (let i = 1; i <= ultimo; i++) {
    const [t1, r1] = perfil[i]!
    if (t <= t1) {
      const [t0, r0] = perfil[i - 1]!
      const f = (t - t0) / (t1 - t0)
      // suaviza a passagem entre pontos do perfil, para a silhueta não ter vinco
      const suave = f * f * (3 - 2 * f)
      return r0 + (r1 - r0) * suave
    }
  }
  return perfil[ultimo]![1]
}

const SEGMENTOS_RADIAIS = 12
const AMOSTRAS_DO_CORPO = 7
const PASSOS_DA_CALOTA = 3

/**
 * Perfil (raio, altura) de um membro de revolução, de baixo para cima (a ordem
 * que o three espera para ter as normais voltadas para fora).
 *
 * O pivô é o centro de uma calota esférica no topo, que sobe `raio` acima dele:
 * é a "bola" da articulação, que preenche o vinco com o segmento de cima quando
 * o membro estica e acompanha a dobra quando ele flexiona. A ponta de baixo é
 * um disco no raio do início do segmento seguinte, coberto pela bola dele.
 */
export function perfilDoMembro(forma: FormaDeMembro, comprimento: number, raio: number): Vector2[] {
  const perfil = PERFIS_DE_MEMBRO[forma]
  const rTopo = raio * raioNoPerfil(perfil, 0)

  // a ponta é fechada por um disco: sem ele, o punho e o tornozelo mostram o vazio por dentro
  const pontos: Vector2[] = [new Vector2(0, -comprimento)]
  for (let i = AMOSTRAS_DO_CORPO; i >= 1; i--) {
    const t = i / AMOSTRAS_DO_CORPO
    pontos.push(new Vector2(raio * raioNoPerfil(perfil, t), -comprimento * t))
  }
  for (let i = 0; i <= PASSOS_DA_CALOTA; i++) {
    const a = (i / PASSOS_DA_CALOTA) * (Math.PI / 2)
    pontos.push(new Vector2(rTopo * Math.cos(a), rTopo * Math.sin(a)))
  }
  return pontos
}

const CACHE_DE_MEMBROS = new Map<string, BufferGeometry>()

/**
 * Geometria do membro, com o pivô em y = 0 (calota até y = +raio) e a ponta fechada em y = −comprimento. É
 * compartilhada entre todas as vítimas de mesma dimensão e nunca é descartada:
 * são poucas combinações, uma por perfil de vítima.
 */
export function geometriaDeMembro(forma: FormaDeMembro, comprimento: number, raio: number): BufferGeometry {
  const chave = `${forma}|${comprimento.toFixed(4)}|${raio.toFixed(4)}`
  let geometria = CACHE_DE_MEMBROS.get(chave)
  if (!geometria) {
    geometria = new LatheGeometry(perfilDoMembro(forma, comprimento, raio), SEGMENTOS_RADIAIS)
    CACHE_DE_MEMBROS.set(chave, geometria)
  }
  return geometria
}

// ───────────────────────────── tronco ─────────────────────────────

/**
 * Seção do tronco, da cintura aos ombros: raio relativo à meia-largura (e à
 * meia-profundidade) do perfil, em função da altura relativa. Estreita na
 * cintura, alarga no peito e fecha na base do pescoço.
 */
const PERFIL_DO_TRONCO: PerfilRelativo = [
  [0, 0.9],
  [0.18, 0.84],
  [0.34, 0.85],
  [0.55, 0.95],
  [0.76, 1.02],
  [0.92, 0.95],
  [1, 0.5],
]

/** Tronco de seção elíptica, de y = 0 (cintura) a y = alturaTronco (base do pescoço). */
export function geometriaDoTronco(p: Pick<ProporcoesVitima, 'alturaTronco' | 'larguraTronco' | 'profundidadeTronco'>): BufferGeometry {
  const chave = `tronco|${p.alturaTronco}|${p.larguraTronco}|${p.profundidadeTronco}`
  let geometria = CACHE_DE_MEMBROS.get(chave)
  if (!geometria) {
    const pontos: Vector2[] = [new Vector2(0, -0.02)]
    for (const [t, r] of PERFIL_DO_TRONCO) pontos.push(new Vector2(r, p.alturaTronco * t))
    pontos.push(new Vector2(0, p.alturaTronco * 1.03))
    geometria = new LatheGeometry(pontos, 20)
    geometria.scale(p.larguraTronco / 2, 1, p.profundidadeTronco / 2)
    CACHE_DE_MEMBROS.set(chave, geometria)
  }
  return geometria
}

// ───────────────────── rosto e mão, fundidos por material ─────────────────────

/**
 * Cada peça do rosto e da mão era uma malha: com 7 a 9 por vítima, a cena do
 * Table top, que repete vítimas, somava dezenas de chamadas de desenho por detalhe.
 * Aqui as peças de mesmo material viram uma geometria só, já posicionada.
 */
function posicionada(
  geometria: BufferGeometry,
  posicao: [number, number, number],
  rotacao: [number, number, number] = [0, 0, 0],
  escala: [number, number, number] = [1, 1, 1],
): BufferGeometry {
  return geometria.applyMatrix4(
    new Matrix4().compose(new Vector3(...posicao), new Quaternion().setFromEuler(new Euler(...rotacao)), new Vector3(...escala)),
  )
}

function fundida(geometrias: BufferGeometry[]): BufferGeometry {
  const resultado = mergeGeometries(geometrias)
  if (!resultado) throw new Error('corpoHumano: geometrias com atributos incompatíveis')
  geometrias.forEach((g) => g.dispose())
  return resultado
}

export interface GeometriasDoRosto {
  readonly pele: BufferGeometry
  readonly olhos: BufferGeometry
  readonly cabelo: BufferGeometry
}

/**
 * Orelhas e nariz (pele), olhos e cabelo de uma cabeça de raio `r`, com a origem
 * no centro da cabeça e o rosto voltado para +z. O cabelo é uma calota inclinada
 * para trás, que deixa a testa à mostra, e, quando comprido, um volume atrás da cabeça.
 */
export function geometriasDoRosto(r: number, cabeloLongo: boolean): GeometriasDoRosto {
  const chave = `rosto|${r}|${cabeloLongo}`
  const guardado = CACHE_DO_ROSTO.get(chave)
  if (guardado) return guardado

  const pele = fundida([
    ...[-1, 1].map((lado) => posicionada(new SphereGeometry(r * 0.2, 8, 8), [lado * r * 0.9, -r * 0.05, -r * 0.05], [0, 0, 0], [0.35, 1, 0.7])),
    posicionada(new ConeGeometry(r * 0.1, r * 0.26, 8), [0, -r * 0.08, r * 0.97], [Math.PI / 2, 0, 0]),
  ])
  const olhos = fundida([-1, 1].map((lado) => posicionada(new SphereGeometry(r * 0.075, 8, 8), [lado * r * 0.33, r * 0.12, r * 0.9])))
  const partesDoCabelo = [
    posicionada(new SphereGeometry(r * 1.045, 18, 12, 0, Math.PI * 2, 0, Math.PI * 0.5), [0, 0, 0], [-0.5, 0, 0], [0.95, 1.1, 1.04]),
  ]
  if (cabeloLongo) partesDoCabelo.push(posicionada(new SphereGeometry(r, 14, 10), [0, -r * 0.4, -r * 0.42], [0, 0, 0], [0.92, 1.25, 0.62]))
  const rosto = { pele, olhos, cabelo: fundida(partesDoCabelo) }
  CACHE_DO_ROSTO.set(chave, rosto)
  return rosto
}

const CACHE_DO_ROSTO = new Map<string, GeometriasDoRosto>()
const CACHE_DA_MAO = new Map<string, BufferGeometry>()

/**
 * Palma achatada e polegar, com a origem no punho e os dedos para baixo (−y). O
 * polegar fica do lado de dentro (−`lado`), voltado para a frente.
 */
export function geometriaDaMao(raioMembro: number, lado: -1 | 1): BufferGeometry {
  const chave = `mao|${raioMembro}|${lado}`
  let geometria = CACHE_DA_MAO.get(chave)
  if (!geometria) {
    const r = raioMembro
    geometria = fundida([
      posicionada(new SphereGeometry(1, 12, 10), [0, -r * 1.1, 0], [0, 0, 0], [r * 0.62, r * 1.3, r * 0.36]),
      posicionada(new SphereGeometry(1, 8, 8), [-lado * r * 0.42, -r * 0.8, r * 0.28], [0.5, 0, 0], [r * 0.2, r * 0.58, r * 0.22]),
    ])
    CACHE_DA_MAO.set(chave, geometria)
  }
  return geometria
}
